/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { CancelablePromise } from '@services/requests/core/CancelablePromise';
import { OpenAPIHousien } from '@services/requests/core/OpenAPI';
import { request as __request } from '@services/requests/core/request';

import type { SaleState } from '../constants/sale-states';
import type {
  CommissionReceipt,
  Sale,
  SaleCommissionPanel,
  SaleCreateInput,
  SaleDocument,
  SaleDocumentCategory,
  SaleListResponse,
  SalePaperworkMilestoneType,
  SaleSettings,
} from '../types/sale.types';

/**
 * Typed client for the sale registry.
 *
 * There is no create method, and that is not an omission: a sale record is
 * opened by the backend when a sale deal is won, with an id derived from
 * that deal. Nothing in the UI may bring one into existence.
 */
export class SaleService {
  public static listSales(
    agencyId: string,
    state?: string,
    page: number = 1,
    pageSize: number = 50,
  ): CancelablePromise<SaleListResponse> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/sales',
      path: { agencyId },
      query: { page, page_size: pageSize, ...(state && { state }) },
    });
  }

  /**
   * Create a sale record with no deal behind it — for a sale that never
   * went through the deal pipeline. `origin_deal_id` is never part of the
   * body; the backend leaves it empty on this path.
   */
  public static createSale(
    agencyId: string,
    body: SaleCreateInput,
  ): CancelablePromise<Sale> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/sales',
      path: { agencyId },
      body,
      mediaType: 'application/json',
    });
  }

  public static getSale(agencyId: string, saleId: string): CancelablePromise<Sale> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/sales/{saleId}',
      path: { agencyId, saleId },
    });
  }

  public static getCommissionPanel(
    agencyId: string,
    saleId: string,
  ): CancelablePromise<SaleCommissionPanel> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/sales/{saleId}/commission',
      path: { agencyId, saleId },
    });
  }

  /**
   * Conclude or cancel. The backend validates the edge and answers 422
   * naming the problem, so no `errors` entry here — a fixed string would
   * discard the only part of the response that tells the broker what is
   * wrong. Same reasoning as the lease service.
   */
  public static applyTransition(
    agencyId: string,
    saleId: string,
    targetState: SaleState,
    reason?: string,
  ): CancelablePromise<Sale> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/sales/{saleId}/transitions',
      path: { agencyId, saleId },
      body: { target_state: targetState, ...(reason && { reason }) },
      mediaType: 'application/json',
    });
  }

  /** Keys must be ids from the agency's configured milestone types. */
  public static recordPaperwork(
    agencyId: string,
    saleId: string,
    dates: Record<string, string>,
  ): CancelablePromise<Sale> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agency/{agencyId}/sales/{saleId}/paperwork',
      path: { agencyId, saleId },
      body: dates,
      mediaType: 'application/json',
    });
  }

  /** The agency's configured paperwork milestone types (add/edit, no remove). */
  public static listPaperworkMilestoneTypes(
    agencyId: string,
  ): CancelablePromise<SalePaperworkMilestoneType[]> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/sales/paperwork-milestone-types',
      path: { agencyId },
    });
  }

  public static addPaperworkMilestoneType(
    agencyId: string,
    body: { label: string; required: boolean },
  ): CancelablePromise<SalePaperworkMilestoneType> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/sales/paperwork-milestone-types',
      path: { agencyId },
      body,
      mediaType: 'application/json',
    });
  }

  public static updatePaperworkMilestoneType(
    agencyId: string,
    milestoneId: string,
    body: { label?: string; required?: boolean },
  ): CancelablePromise<SalePaperworkMilestoneType> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agency/{agencyId}/sales/paperwork-milestone-types/{milestoneId}',
      path: { agencyId, milestoneId },
      body,
      mediaType: 'application/json',
    });
  }

  /** The agency's sale settings: default commission and document vocabulary. */
  public static readSettings(agencyId: string): CancelablePromise<SaleSettings> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/sales/settings',
      path: { agencyId },
    });
  }

  /** A null percentage clears the default; 0 is a sale with no commission. */
  public static updateSettings(
    agencyId: string,
    body: { default_commission_percentage: number | null },
  ): CancelablePromise<SaleSettings> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agency/{agencyId}/sales/settings',
      path: { agencyId },
      body,
      mediaType: 'application/json',
    });
  }

  public static addDocumentCategory(
    agencyId: string,
    body: { label: string },
  ): CancelablePromise<SaleDocumentCategory> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/sales/settings/document-categories',
      path: { agencyId },
      body,
      mediaType: 'application/json',
    });
  }

  public static updateDocumentCategory(
    agencyId: string,
    categoryId: string,
    body: { label: string },
  ): CancelablePromise<SaleDocumentCategory> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agency/{agencyId}/sales/settings/document-categories/{categoryId}',
      path: { agencyId, categoryId },
      body,
      mediaType: 'application/json',
    });
  }

  /** Record an amount that arrived. Appends; never replaces. */
  public static recordCommissionReceipt(
    agencyId: string,
    saleId: string,
    receipt: CommissionReceipt,
  ): CancelablePromise<Sale> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/sales/{saleId}/commission-receipts',
      path: { agencyId, saleId },
      body: receipt,
      mediaType: 'application/json',
    });
  }

  public static listDocuments(
    agencyId: string,
    saleId: string,
    category?: string,
  ): CancelablePromise<{ data: SaleDocument[] }> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/sales/{saleId}/documents',
      path: { agencyId, saleId },
      query: { ...(category && { category }) },
    });
  }

  public static createDocumentUploadLink(
    agencyId: string,
    saleId: string,
    body: { filename: string; content_type: string; category?: string; label?: string },
  ): CancelablePromise<{
    document_id: string;
    filename: string;
    category: string;
    label?: string | null;
    upload_url: { url: string; fields?: Record<string, string> };
  }> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/sales/{saleId}/documents/upload-link',
      path: { agencyId, saleId },
      body,
      mediaType: 'application/json',
    });
  }

  public static getDocumentViewLink(
    agencyId: string,
    saleId: string,
    documentId: string,
  ): CancelablePromise<{ url: string }> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/sales/{saleId}/documents/{documentId}',
      path: { agencyId, saleId, documentId },
    });
  }

  public static deleteDocument(
    agencyId: string,
    saleId: string,
    documentId: string,
  ): CancelablePromise<{ deleted: boolean }> {
    return __request(OpenAPIHousien, {
      method: 'DELETE',
      url: '/agency/{agencyId}/sales/{saleId}/documents/{documentId}',
      path: { agencyId, saleId, documentId },
    });
  }
}
